import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import api from "@/api";
import { setCurrency } from "@/currency";
import { flagEmoji, countryName } from "@/countries";
import { Search, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function CountrySwitcher({ dark = false, tid = "country-switcher-btn" }) {
  const { lang, t } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const current = user?.country || "IT";

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - 296)) });
    }
    setOpen(!open);
  };

  useEffect(() => {
    if (open && list.length === 0) api.countries().then(setList).catch(() => {});
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    const tm = setTimeout(() => inputRef.current?.focus(), 60);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); clearTimeout(tm); };
  }, [open, list.length]);

  const items = useMemo(() => {
    const withNames = list.map((c) => ({ ...c, localName: countryName(c.code, lang) }));
    withNames.sort((a, b) => a.localName.localeCompare(b.localName, lang));
    const s = q.trim().toLowerCase();
    if (!s) return withNames;
    return withNames.filter((c) => c.localName.toLowerCase().includes(s) || c.name.toLowerCase().includes(s) || c.code.toLowerCase().includes(s));
  }, [list, q, lang]);

  const pick = async (c) => {
    if (busy || c.code === current) { setOpen(false); return; }
    setBusy(true);
    try {
      const r = await api.setBusinessCountry(c.code);
      setCurrency(r.symbol);
      if (r.lang) localStorage.setItem("lang", r.lang);
      toast.success(`${flagEmoji(c.code)} ${countryName(c.code, lang)} — ${t("countryApplied")}`);
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
      setBusy(false);
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-sm transition-colors ${dark ? "hover:bg-white/10 text-white/80" : "hover:bg-muted text-foreground"}`}
        title={t("country")}
        data-testid={tid}
      >
        <span className="text-base leading-none">{flagEmoji(current)}</span>
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>
      {open && createPortal(
        <div ref={panelRef} style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 1000 }} className="w-72 bg-white text-foreground rounded-xl shadow-xl border border-border overflow-hidden" data-testid="country-switcher-panel">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("searchCountry")}
              className="flex-1 text-sm outline-none bg-transparent"
              data-testid="country-search-input"
            />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {items.map((c) => (
              <button
                key={c.code}
                onClick={() => pick(c)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-muted ${c.code === current ? "bg-muted/60 font-semibold" : ""}`}
                data-testid={`country-option-${c.code}`}
              >
                <span className="text-base">{flagEmoji(c.code)}</span>
                <span className="flex-1 truncate">{c.localName}</span>
                <span className="text-xs text-muted-foreground">{c.symbol}</span>
                {c.code === current && <Check className="w-3.5 h-3.5 text-primary" />}
              </button>
            ))}
            {items.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground">—</div>}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
